import React from 'react';
import _ from 'lodash';
import { ApolloProvider } from 'react-apollo';
import ApolloClient from 'apollo-client';
import { createHttpLink } from 'apollo-link-http';
import { InMemoryCache, IntrospectionFragmentMatcher } from 'apollo-cache-inmemory';
import PageConfigCacheContainer from 'containers/PageConfigCacheContainer';
import {
  BrowserRouter as Router,
  Route,
} from 'react-router-dom';
import { CookiesProvider } from 'react-cookie';
import { func } from 'prop-types';
import Routes from 'routes';
import fetch from 'unfetch';
import { Provider as UnstatedProvider } from 'unstated';
import { StripeProvider } from 'react-stripe-elements';
import { getRuntimeEnvVariables } from 'helpers/Utils';
import { TrackingProvider } from 'components/tracking';
import withTracking from 'components/tracking/withTracking';
import SupportButton from 'components/supportButton/SupportButton';
import { FreezeProvider, FreezeComponentCount } from 'components/common/Freeze';
import Experiment, { ExperimentProvider } from 'components/abTest/Experiment';
import { LazyLoadImgProvider } from 'components/common/LazyLoadImg';
import { UserAgentProvider } from 'containers/withUserAgent';
import introspectionQueryResultData from 'queries/fragmentTypes.json';
import { getCLS, getFID, getLCP } from 'web-vitals';
import { cookieCagIdTrackEvent } from 'components/tracking/TrackingUtils';
import RaThemeProvider from './RaThemeProvider';
import ApplicationConfigProvider from './ApplicationConfigProvider';
import LanguageCookie from './LanguageCookie';
import ScrollToTop from './ScrollToTop';

import './App.scss';

const unstatedContainers = [
  new PageConfigCacheContainer(),
];

const ssrFreezeComponentCount = new FreezeComponentCount();
// eslint-disable-next-line
ssrFreezeComponentCount.value = window.__SSR_FREEZE_COMPONENT_COUNT__ || 0;

// eslint-disable-next-line
const userAgent = window?.__USER_AGENT__ || {};

// eslint-disable-next-line
const appConfig = window?.__APP_CONFIG__ || {};

// eslint-disable-next-line
const unstatedInitialStates = window.__UNSTATED_INITIAL_STATES__ || [];
const unstatedContainersWithInitialStates = unstatedContainers.map((container, i) => {
  const cloneContainer = _.cloneDeep(container);
  cloneContainer.state = unstatedInitialStates[i];
  return cloneContainer;
});

const defaultOptions = {
  watchQuery: {
    fetchPolicy: 'network-only',
    errorPolicy: 'all',
  },
  query: {
    fetchPolicy: 'network-only',
    errorPolicy: 'all',
  },
};

const fragmentMatcher = new IntrospectionFragmentMatcher({
  introspectionQueryResultData,
});

const cache = new InMemoryCache({ fragmentMatcher });

const apolloClient = new ApolloClient({
  // https://www.apollographql.com/docs/react/features/server-side-rendering.html
  // eslint-disable-next-line
  cache: cache.restore(window.__APOLLO_STATE__),
  ssrForceFetchDelay: 500,
  // eslint-disable-next-line
  link: new createHttpLink({
    fetch,
    uri: getRuntimeEnvVariables('RUNTIME_CLIENT_GRAPHQL_SERVER'),
    credentials: 'include',
  }),
  connectToDevTools: process.env.NODE_ENV !== 'production',
  defaultOptions,
});

class App extends React.Component {
  constructor() {
    super();
    this.state = { stripe: null };
  }

  componentDidMount() {
    const { track } = this.props;
    cookieCagIdTrackEvent(track);

    // Init stripe on client side
    if (window.Stripe) {
      this.setState({ stripe: window.Stripe(getRuntimeEnvVariables('RUNTIME_STRIPE_API_KEY')) });
    } else {
      const stripeJsDom = document.querySelector('#stripe-js');
      if (stripeJsDom) {
        stripeJsDom.addEventListener('load', () => {
          // Create Stripe instance once Stripe.js loads
          this.setState({ stripe: window.Stripe(getRuntimeEnvVariables('RUNTIME_STRIPE_API_KEY')) });
        });
      }
    }

    // web-vitals
    if (typeof window !== 'undefined') {
      window.dataLayer = window.dataLayer || [];

      const pushWebVitalsToDatalayer = ({ name, delta, id }) => {
        window.dataLayer.push({
          event: 'web-vitals',
          vitals_name: name,
          vitals_metric: Math.round(name === 'CLS' ? delta * 1000 : delta),
          vitals_id: id,
        });
      };

      getCLS(pushWebVitalsToDatalayer);
      getFID(pushWebVitalsToDatalayer);
      getLCP(pushWebVitalsToDatalayer);
    }
  }

  render() {
    const { stripe } = this.state;
    return (
      <ApolloProvider client={apolloClient}>
        <UnstatedProvider inject={unstatedContainersWithInitialStates}>
          <UserAgentProvider userAgent={userAgent}>
            <CookiesProvider>
              <FreezeProvider ssrFreezeComponentCount={ssrFreezeComponentCount}>
                <LazyLoadImgProvider>
                  <Router>
                    <ExperimentProvider experiments={window?.experiments}>
                      <RaThemeProvider>
                        <ScrollToTop>
                          <Route
                            path="/:language?"
                            render={() => (
                              <ApplicationConfigProvider
                                appConfig={appConfig}
                              >
                                <TrackingProvider
                                  gtmId={getRuntimeEnvVariables('RUNTIME_GTMID')}
                                >
                                  <SupportButton
                                    zenKey={getRuntimeEnvVariables('RUNTIME_ZENDESK_KEY')}
                                  />
                                  <LanguageCookie>
                                    <StripeProvider
                                      stripe={stripe}
                                    >
                                      {/* TODO: remove experiment component
                                          after server cache AB test */}
                                      <Experiment
                                        experimentId={getRuntimeEnvVariables('RUNTIME_NODE_CACHE_TEST')}
                                        controlVariantId={getRuntimeEnvVariables('RUNTIME_NODE_CACHE_TEST_CONTROL')}
                                        variantIds={(getRuntimeEnvVariables('RUNTIME_NODE_CACHE_TEST_VARIANTS') || '').split(',')}
                                      >
                                        {() => (
                                          <Routes />
                                        )}
                                      </Experiment>
                                    </StripeProvider>
                                  </LanguageCookie>
                                </TrackingProvider>
                              </ApplicationConfigProvider>
                            )}
                          />
                        </ScrollToTop>
                      </RaThemeProvider>
                    </ExperimentProvider>
                  </Router>
                </LazyLoadImgProvider>
              </FreezeProvider>
            </CookiesProvider>
          </UserAgentProvider>
        </UnstatedProvider>
      </ApolloProvider>
    );
  }
}

App.propTypes = {
  track: func,
};

App.defaultProps = {
  track: null,
};

export default withTracking(App);
